import React, { FC, useEffect, useState } from 'react';
import { Modal, Pressable, Switch, View } from 'react-native';

import DesignSystemButton from '../Button/DesignSystemButton';
import { Body1Bold, ParagraphMBold } from '../../config/designSystem/typography';
import { COLORS } from '../../config/designSystem/Colors';
import { SPACING } from '../../config/designSystem/Spacing';
import { SelectField } from '../Diary/ui/SelectField';
import { TouchableText } from '../TouchableText/TouchableText';

/**
 * figma: the ⚙️ challenge settings of the Activity Page (node 9693-21682):
 * daily reminder (toggle + schedule) and removing the challenge from the
 * program.
 */
interface ChallengeSettingsSheetProps {
  visible: boolean;
  onClose: () => void;
  reminderEnabled: boolean;
  reminderTime: string;
  onSaveReminder: (enabled: boolean, time: string) => void;
  onStopChallenge: () => void;
  labels?: {
    SETTINGS_TITLE?: string;
    ACTIVATE_REMINDERS?: string;
    SCHEDULE?: string;
    STOP_CHALLENGE?: string;
    SAVE?: string;
  };
}

const TIME_OPTIONS = [
  '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM',
  '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM',
];

// the API stores and compares reminder times as 24-hour 'HH:mm'
// (see sendDueReminders); the sheet displays 12-hour labels
export const displayTimeTo24h = (display: string): string => {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(display || '');
  if (!match) {
    return display;
  }
  let hours = parseInt(match[1], 10) % 12;
  if (match[3].toUpperCase() === 'PM') {
    hours += 12;
  }
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
};

export const time24hToDisplay = (time: string): string => {
  const match = /^(\d{2}):(\d{2})$/.exec(time || '');
  if (!match) {
    return time;
  }
  const hours = parseInt(match[1], 10);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${match[2]} ${suffix}`;
};

export const ChallengeSettingsSheet: FC<ChallengeSettingsSheetProps> = ({
  visible,
  onClose,
  reminderEnabled,
  reminderTime,
  onSaveReminder,
  onStopChallenge,
  labels,
}) => {
  const [enabled, setEnabled] = useState(reminderEnabled);
  const [time, setTime] = useState(time24hToDisplay(reminderTime) || '6:00 PM');

  // the reminder state arrives from a fetch that may land after first mount:
  // re-seed the sheet from props whenever it opens (or they change) so Save
  // can never silently persist a stale off/default state
  useEffect(() => {
    setEnabled(reminderEnabled);
    setTime(time24hToDisplay(reminderTime) || '6:00 PM');
  }, [visible, reminderEnabled, reminderTime]);

  return (
    <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.4)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: SPACING['sp-16'],
        }}
      >
        <Pressable
          onPress={() => undefined}
          style={{
            width: '100%',
            maxWidth: 420,
            backgroundColor: COLORS.surface.contrast,
            borderRadius: SPACING['sp-16'],
            padding: SPACING['sp-24'],
            rowGap: SPACING['sp-24'],
          }}
        >
          <Body1Bold>{labels?.SETTINGS_TITLE || 'Challenge settings'}</Body1Bold>

          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <ParagraphMBold>{labels?.ACTIVATE_REMINDERS || 'Activate reminders'}</ParagraphMBold>
            <Switch
              value={enabled}
              onValueChange={setEnabled}
              trackColor={{ true: COLORS.content.accent, false: COLORS.border.secondary }}
              thumbColor={COLORS.surface.contrast}
            />
          </View>

          {enabled && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', columnGap: SPACING['sp-16'] }}>
              <ParagraphMBold>{labels?.SCHEDULE || 'Schedule'}</ParagraphMBold>
              {/* SelectField's root is flex:1 and expects a ROW parent (it
                  shares width in the diary forms); in a column parent it
                  collapses and its value/chevron lay out at zero size */}
              <View style={{ width: 150, flexDirection: 'row' }}>
                <SelectField label="" value={time} options={TIME_OPTIONS} onChange={setTime} />
              </View>
            </View>
          )}

          <DesignSystemButton
            label={labels?.SAVE || 'Save'}
            onPress={() => {
              onSaveReminder(enabled, displayTimeTo24h(time));
              onClose();
            }}
          />

          <View style={{ alignItems: 'center' }}>
            <TouchableText
              onPress={() => {
                onStopChallenge();
                onClose();
              }}
              label={labels?.STOP_CHALLENGE || 'Stop challenge'}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};
